import { useCallback, useEffect, useRef, useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import {
  fakeSMTEntryDetailsForFood,
  fakeSMTEntryProps,
  fakeSMTEntryType,
} from "@/__mocks__/SMT";
import { SMTEntryType } from "@/constants/SMT";
import type {
  SMTEntryDetails,
  SMTEntryProps,
} from "@/ui/components/smt/ViewSMTEntries/SMTEntry";
import SMTEntry from "@/ui/components/smt/ViewSMTEntries/SMTEntry";
import { Button } from "@/ui/core";
import { Box, VStack } from "@/ui/layout";

const StoryMeta: Meta<{ entryType: SMTEntryType } & SMTEntryProps> = {
  title: "Components/SMT/SMT Entry",
  component: SMTEntry,
  argTypes: {
    avatarUserName: { control: "text" },
    entryType: { control: "select", options: Object.values(SMTEntryType) },
    time: { control: "text" },
  },
  args: {
    avatarUserName: "Firstname Lastname",
    entryType: SMTEntryType.Meal,
    time: "8:12am",
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const [regenerationCount, setRegenerationCount] = useState<number>(1);
    const getFakeProps = useCallback(() => {
      // Record type forces updating this as new SMTEntryTypes are added
      const detailsOptions: Record<SMTEntryType, SMTEntryDetails> = {
        meal: fakeSMTEntryDetailsForFood(),
      };

      const entryType = fakeSMTEntryType();
      return {
        ...fakeSMTEntryProps(),
        details: detailsOptions[entryType],
        entryType,
      };
    }, []);
    const getFakePropsRef = useRef<() => SMTEntryProps>(getFakeProps);
    useEffect(() => {
      getFakePropsRef.current = getFakeProps;
    }, [getFakeProps]);

    // allow both re-generation of fake props, and use of storybook controls
    const [props, setProps] = useState<SMTEntryProps>({
      ...getFakeProps(),
      ...args,
    });
    useEffect(() => {
      setProps((current) => ({ ...current, ...args }));
    }, [args]);
    useEffect(() => {
      setProps((current) => ({ ...current, ...getFakePropsRef.current() }));
    }, [regenerationCount]);

    return (
      <VStack gap={50} padding={24} width="100%">
        <SMTEntry {...props} />
        <Box alignItems="center">
          <Button
            onPress={() => {
              // force new render, which will force new faker mock calls to generate new data
              setRegenerationCount((count) => count + 1);
            }}
            testID="storybook"
            title="Generate new fake data (+ photo)"
          />
        </Box>
      </VStack>
    );
  },
};

type Story = StoryObj<SMTEntryProps>;

const Default: Story = {};

export { Default };
export default StoryMeta;
